/**
 * Localization Context
 * Provides language switching functionality throughout the app
 */

import { createContext, useContext, ReactNode, useCallback, useState } from 'react';
import { useUserContext } from './UserContext';
import { api } from '../services/api';

interface LocalizationContextType {
  language: string;
  setLanguage: (lang: string) => Promise<void>;
  isChangingLanguage: boolean;
}

const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined);

export function LocalizationProvider({ children }: { children: ReactNode }) {
  const { user, updateUser } = useUserContext();
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  
  // Get current language from user or default to 'ru'
  const language = user?.language || 'ru';
  
  const setLanguage = useCallback(async (lang: string) => {
    if (lang === language) {
      return; // No change needed
    }
    
    // Type guard to ensure lang is 'ru' or 'en'
    if (lang !== 'ru' && lang !== 'en') {
      throw new Error('Invalid language. Must be "ru" or "en"');
    }
    
    try {
      setIsChangingLanguage(true);
      
      // Update language on server
      await api.updateSettings({ language: lang });
      
      // Update local user state
      updateUser({ language: lang });
    } catch (err) {
      console.error('Failed to update language:', err);
      throw err;
    } finally {
      setIsChangingLanguage(false);
    }
  }, [language, updateUser]);
  
  return (
    <LocalizationContext.Provider
      value={{
        language,
        setLanguage,
        isChangingLanguage,
      }}
    >
      {children}
    </LocalizationContext.Provider>
  );
}

export function useLocalization() {
  const context = useContext(LocalizationContext);
  if (context === undefined) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }
  return context;
}
